import { useMemo } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Building2, Plus, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/common/components/ui/button';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog.tsx';
import { organizationQueries } from '@/features/organizations/queries';
import { BillingAddressCard } from '@/features/billing-addresses/components/billing-address-card/BillingAddressCard';
import { useBillingAddressDialog } from '@/features/billing-addresses/components/billing-address-dialog/useBillingAddressDialog';
import { BillingSection } from '@/features/billing-addresses/components/billing-section/BillingSection';
import { BillingAddressService } from '@/features/billing-addresses/services/BillingAddressService';
import {
  billingAddressQueries,
  billingAddressQueryKeys,
} from '@/features/billing-addresses/queries';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';
import type {
  BillingAddress,
  BillingAddressOwner,
} from '@/features/billing-addresses/services/schemas';

export function BillingPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();
  const currentUser = useCurrentUser();

  const { data: orgs } = useSuspenseQuery(organizationQueries.list());
  const { data: allAddresses } = useSuspenseQuery(billingAddressQueries.list());
  const { data: allMembers } = useSuspenseQuery(organizationQueries.membersList());

  const openBillingAddressDialog = useBillingAddressDialog(orgs);

  const personalAddresses = allAddresses.filter((a) => a.partner === null);

  // Group org billing addresses by org ID — the org list endpoint does not embed them.
  const orgAddressMap = useMemo(() => {
    const map = new Map<number, typeof allAddresses>();
    for (const address of allAddresses) {
      if (address.partner !== null) {
        const existing = map.get(address.partner);
        if (existing) {
          existing.push(address);
        } else {
          map.set(address.partner, [address]);
        }
      }
    }
    return map;
  }, [allAddresses]);

  // Derive org add permission from membership — works even when org has no addresses yet.
  // Both 'full' (admin) and 'write' (editor) roles can add billing addresses.
  const adminOrgIds = useMemo(
    () =>
      new Set(
        allMembers
          .filter(
            (m) => m.user.email === currentUser.email && (m.role === 'full' || m.role === 'write')
          )
          .map((m) => m.partner)
      ),
    [allMembers, currentUser.email]
  );

  const canEditAddress = (address: BillingAddress) => address.authorizedRole === 'full';

  const deleteMutation = useMutation({
    mutationFn: (address: BillingAddress) => BillingAddressService.delete(address.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingAddressQueryKeys.all });
    },
    onError: () => toast.error(t`Failed to delete address`),
  });

  const handleAdd = async (owner?: BillingAddressOwner) => {
    const saved = await openBillingAddressDialog({ owner });
    if (saved) queryClient.invalidateQueries({ queryKey: billingAddressQueryKeys.all });
  };

  const handleEdit = async (address: BillingAddress) => {
    const saved = await openBillingAddressDialog({ address });
    if (saved) queryClient.invalidateQueries({ queryKey: billingAddressQueryKeys.all });
  };

  const handleDelete = async (address: BillingAddress) => {
    const name = address.name ?? '';
    const confirmed = await confirm({
      title: t`Delete address?`,
      description: t`"${name}" will be permanently removed.`,
      confirmLabel: t`Delete`,
    });
    if (confirmed) deleteMutation.mutate(address);
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            <Trans>Billing</Trans>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            <Trans>Manage personal and organization billing addresses.</Trans>
          </p>
        </div>

        <Button onClick={() => handleAdd()} className="gap-1.5 shrink-0 self-start sm:self-auto">
          <Plus size={15} />
          <Trans>Add address</Trans>
        </Button>
      </div>

      <BillingSection
        icon={User}
        iconBgClassName="bg-muted"
        iconClassName="text-muted-foreground"
        title={t`Personal addresses`}
        subtitle={t`Private billing addresses visible only to you.`}
        addressCount={personalAddresses.length}
        canAdd={true} // Personal addresses: always allowed
        onAdd={() => handleAdd()}
      >
        {personalAddresses.map((address) => (
          <BillingAddressCard
            key={address.id}
            address={address}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}
      </BillingSection>

      {orgs.map((org) => {
        const canAdd = adminOrgIds.has(org.id);
        const addresses = orgAddressMap.get(org.id) ?? [];
        return (
          <BillingSection
            key={org.id}
            icon={Building2}
            iconBgClassName="bg-primary/10"
            iconClassName="text-primary"
            title={org.name}
            addressCount={addresses.length}
            canAdd={canAdd}
            onAdd={() => handleAdd({ orgId: org.id })}
            emptyMessage={
              <Trans>No billing addresses. Ask an organization admin to add one.</Trans>
            }
          >
            {addresses.map((address) => {
              const editable = canEditAddress(address);
              return (
                <BillingAddressCard
                  key={address.id}
                  address={address}
                  readonlyMessage={
                    !editable ? <Trans>Only organization admin can edit</Trans> : undefined
                  }
                  onEdit={editable ? handleEdit : undefined}
                  onDelete={editable ? handleDelete : undefined}
                />
              );
            })}
          </BillingSection>
        );
      })}
    </div>
  );
}
